import { Box } from "@mui/material";
import { lazy } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import ModuleRouteGuard from "../common/components/ModuleRouteGuard";
import MainLayout from "./layouts/main-layout";
import PrivateRoute from "./private-route";

const AllEmployees = lazy(
  () => import("../modules/EmployerManagement/All Employees"),
);
const EmployeeBasicDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/BasicsDetails"),
);
const EmployeeInvoices = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Invoices"),
);
const EmployeeMemberships = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships"),
);
const MembershipDetails = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Memberships/MembershipDetails"),
);
const EmployeeNotes = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notes"),
);
const EmployeeNotifications = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/Notifications"),
);
const EmployeePaymentOptions = lazy(
  () =>
    import("../modules/EmployerManagement/All Employees/EmployeeMain/Tabs/PaymentOptions"),
);
const ViewPlan = lazy(
  () => import("../modules/EmployerManagement/Settings/Tabs/Plans/ViewPlan"),
);
const EmployeeUploads = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads"),
);
const DetailedRows = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads/Tabs/DetailedRows"),
);
const ProcessedRows = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/EmployeeUploads/Tabs/ProcessedRows"),
);
const EmployerEmployeeMainWrapperForPortal = lazy(() =>
  import("../modules/EmployerManagement/employers/employerMain/Tabs/Employees/EmployeeMain").then(
    (m) => ({ default: m.EmployerEmployeeMainWrapperForPortal }),
  ),
);
const PaymentOption = lazy(
  () =>
    import("../modules/EmployerManagement/employers/employerMain/Tabs/PaymentOption"),
);
const Invoice = lazy(() => import("../modules/EmployerPortal/Invoice"));
const EmployerMemberships = lazy(
  () => import("../modules/EmployerPortal/Membership"),
);
const EmployerPortalNotification = lazy(
  () => import("../modules/EmployerPortal/Notifications"),
);
const SettingsWrapper = lazy(
  () => import("../modules/EmployerPortal/Settings"),
);
const EmployerDashboard = lazy(
  () => import("../modules/EmployerPortal/Dashboard/index"),
);
const AdminList = lazy(
  () => import("../modules/EmployerPortal/Settings/Tabs/AdminList"),
);
const NotificationPreference = lazy(
  () =>
    import("../modules/EmployerPortal/Settings/Tabs/NotificationPrefernace"),
);
const SignupWithPHP = lazy(
  () => import("./employer-management/employers/sign-up"),
);
const EMPProfile = lazy(
  () => import("../modules/EmployerPortal/Settings/Tabs/Profile"),
);
const EmployerCoveragePlan = lazy(
  () => import("../modules/EmployerPortal/CoveragePlans"),
);
const EmployerInvoiceLayout = lazy(
  () => import("../common/components/InvoiceLayout/EmployerInvoiceLayout"),
);
const TicketsModule = lazy(
  () => import("../modules/EmployerManagement/Tickets"),
);
const TicketDashboard = lazy(
  () => import("../modules/EmployerManagement/Tickets/Dashboard"),
);
const TicketList = lazy(
  () => import("../modules/EmployerManagement/Tickets/Tickets"),
);
const TicketConversation = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/Conversation"),
);
const TicketTasks = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/Tasks"),
);
const TicketActivityLogs = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/ActivityLogs"),
);
const TicketNotes = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/Notes"),
);
const TicketAttachments = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/Attachments"),
);
const LinkedTickets = lazy(
  () => import("../modules/EmployerManagement/Tickets/Detail/LinkedTickets"),
);
const AllTickets = lazy(
  () => import("../modules/EmployerManagement/Tickets/Employers/AllTickets"),
);
const Tasks = lazy(
  () => import("../modules/EmployerManagement/Tickets/Employers/Tasks"),
);
const Notes = lazy(
  () => import("../modules/EmployerManagement/Tickets/Employers/Notes"),
);
const TicketManagement = lazy(
  () => import("../modules/EmployerManagement/Tickets/Management"),
);
const AdminTicketsList = lazy(
  () => import("../modules/EmployerPortal/Tickets/AdminsTicket"),
);
const AdminOverview = lazy(
  () => import("../modules/EmployerPortal/Tickets/Employers"),
);
const AllTicketsAdmin = lazy(
  () => import("../modules/EmployerPortal/Tickets/Employers/AllTickets"),
);
const Attachments = lazy(
  () => import("../modules/EmployerPortal/Tickets/Employers/Attachments"),
);
const TicketProvider = lazy(() =>
  import("../modules/EmployerManagement/Tickets/components/TicketContext").then(
    (m) => ({ default: m.TicketProvider }),
  ),
);
const ActivityLogs = lazy(
  () => import("../modules/EmployerManagement/Tickets/Activity/ActivitityLogs"),
);
const MainRoute = () => (
  <PrivateRoute>
    <MainLayout>
      <Outlet />
    </MainLayout>
  </PrivateRoute>
);

const EmployerPortalRoutes = (
  <Route
    path="/employer-portal/employers/:employerUuid"
    element={<MainRoute />}
  >
    <Route
      path="dashboard"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="dashboard">
          <EmployerDashboard />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="all-employees"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="all-employees"
        >
          <AllEmployees feature="EMP All employees" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="invoices"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="invoices">
          <Invoice />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="payment-option"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="payment-option"
        >
          <Box mt={2}>
            <PaymentOption feature="EMP Payment Option" />
          </Box>
        </ModuleRouteGuard>
      }
    />
    <Route
      path="notifications"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="notifications"
        >
          <EmployerPortalNotification />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="memberships"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="memberships">
          <EmployerMemberships />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="memberships/:id"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="memberships">
          <ViewPlan />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="tickets"
      element={
        <TicketProvider>
          <TicketsModule />
        </TicketProvider>
      }
    >
      <Route index element={<Navigate to="dashboard" replace />} />

      <Route path="dashboard" element={<TicketDashboard />} />
      <Route path="activity" element={<ActivityLogs />} />

      <Route path="list">
        <Route index element={<TicketList />} />
        <Route path=":status" element={<TicketList />} />
      </Route>

      <Route path="employers" element={<AdminTicketsList />} />
      <Route path="employers/:childEmployerId" element={<AdminOverview />}>
        <Route index element={<Navigate to="all-tickets" replace />} />
        <Route path="all-tickets" element={<AllTickets />} />
        <Route path="activity" element={<ActivityLogs />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="attachments" element={<Attachments />} />
        <Route path="notes" element={<Notes />} />
      </Route>

      <Route path=":ticketId" element={<TicketManagement />}>
        <Route index element={<Navigate to="conversation" replace />} />
        <Route path="conversation" element={<TicketConversation />} />
        <Route path="tasks" element={<TicketTasks />} />
        <Route path="activity-logs" element={<TicketActivityLogs />} />
        <Route path="notes" element={<TicketNotes />} />
        <Route path="attachments" element={<TicketAttachments />} />
        <Route path="linked-tickets" element={<LinkedTickets />} />
      </Route>
    </Route>

    <Route path="management" element={<AdminOverview />}>
      <Route index element={<Navigate to="all-tickets" replace />} />
      <Route path="all-tickets" element={<AllTicketsAdmin />} />
      <Route path="activity" element={<TicketActivityLogs />} />
      <Route path="tasks" element={<Tasks />} />
      <Route path="attachments" element={<Attachments />} />
      <Route path="notes" element={<Notes />} />
    </Route>

    <Route
      path="employee-uploads"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="employee-uploads"
        >
          <EmployeeUploads feature="EMP Employee Uploads" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="coverage-plans"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="coverage-plans"
        >
          <EmployerCoveragePlan feature="EMP Coverage Plan" />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="settings"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="emp-settings">
          <SettingsWrapper />
        </ModuleRouteGuard>
      }
    >
      <Route
        path="profile"
        element={
          <ModuleRouteGuard moduleType="employer-portal" routePath="profile">
            <EMPProfile />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="adminlist"
        element={
          <ModuleRouteGuard moduleType="employer-portal" routePath="adminlist">
            <AdminList />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notification"
        element={
          <ModuleRouteGuard
            moduleType="employer-portal"
            routePath="emp-notification"
          >
            <NotificationPreference />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="employee-uploads/:uploadUuid"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="employee-uploads"
        >
          <ProcessedRows />
        </ModuleRouteGuard>
      }
    />
    <Route
      path="employee-uploads/:uploadUuid/view-all"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="employee-uploads"
        >
          <DetailedRows />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="sign-up"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="all-employees"
        >
          <SignupWithPHP />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="employees/:employeeUuid"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="all-employees"
        >
          <EmployerEmployeeMainWrapperForPortal />
        </ModuleRouteGuard>
      }
    >
      <Route
        path="employee-details"
        element={
          <ModuleRouteGuard
            moduleType="employer-portal"
            routePath="all-employees"
          >
            <EmployeeBasicDetails feature="EMP Basic Details" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="invoices"
        element={
          <ModuleRouteGuard moduleType="employer-portal" routePath="invoices">
            <EmployeeInvoices feature="EMP Invoices" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="memberships"
        element={
          <ModuleRouteGuard
            moduleType="employer-portal"
            routePath="employee-memberships"
          >
            <EmployeeMemberships feature="EMP Employee Memberships" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="payment-options"
        element={
          <ModuleRouteGuard
            moduleType="employer-portal"
            routePath="payment-option"
          >
            <EmployeePaymentOptions feature="" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notes"
        element={
          <ModuleRouteGuard moduleType="employer-portal" routePath="tickets">
            <EmployeeNotes feature="EMP Notes" />
          </ModuleRouteGuard>
        }
      />
      <Route
        path="notifications"
        element={
          <ModuleRouteGuard
            moduleType="employer-portal"
            routePath="notifications"
          >
            <EmployeeNotifications feature="EMP Employee Notifications" />
          </ModuleRouteGuard>
        }
      />
    </Route>
    <Route
      path="all-employees/:employeeUuid/membership-details"
      element={
        <ModuleRouteGuard
          moduleType="employer-portal"
          routePath="employee-memberships"
        >
          <MembershipDetails feature="EMP Employee Memberships" />
        </ModuleRouteGuard>
      }
    />

    <Route
      path="invoices/invoice/:invoiceUuid/:mode?"
      element={
        <ModuleRouteGuard moduleType="employer-portal" routePath="invoices">
          <EmployerInvoiceLayout isReadOnly />
        </ModuleRouteGuard>
      }
    />
  </Route>
);

export default EmployerPortalRoutes;
